import React, { useState } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent,
  IonList, IonFab, IonFabButton, IonIcon, IonSpinner,
  IonInfiniteScroll, IonInfiniteScrollContent, useIonAlert,
  IonSegment, IonSegmentButton, IonLabel, IonButton,
} from '@ionic/react';
import { add, grid, list, imageOutline, settingsOutline, searchOutline, closeOutline } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { useCards, useUpdateCard } from '../hooks/useCards';
import { usePreferredGames } from '../hooks/useGames';
import { useCreateEvent } from '../hooks/useInventory';
import { Card, CardFilters } from '../types';
import CardItem from '../components/CardItem';
import { API_BASE } from '../config/api';

const resolveImageUrl = (url?: string) =>
  url && url.startsWith('/uploads') ? `${API_BASE}${url}` : url;

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', fontSize: 16,
  border: '1px solid var(--ion-color-medium)', borderRadius: 8,
  background: 'var(--ion-item-background, #fff)',
  color: 'var(--ion-text-color)',
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = { ...inputStyle, appearance: 'auto' as any };

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 4, fontSize: 14,
  fontWeight: 600, color: 'var(--ion-color-medium)',
};

const CardList: React.FC = () => {
  const [filters, setFilters] = useState<CardFilters>({ page: 1, per_page: 20 });
  const [viewMode, setViewMode] = useState<'list' | 'gallery'>('list');
  const [showSearch, setShowSearch] = useState(false);
  const [searchForm, setSearchForm] = useState({
    search: '',
    game_id: 0,
    rarity: '',
    value_op: '' as string,
    value_amount: '' as string,
  });
  const { data, isLoading } = useCards(filters);
  const { data: games = [] } = usePreferredGames();
  const history = useHistory();
  const createEvent = useCreateEvent();
  const updateCard = useUpdateCard();
  const [presentAlert] = useIonAlert();

  const selectedGame = games.find((g) => g.id === searchForm.game_id);

  const handleSearchApply = () => {
    const newFilters: CardFilters = { page: 1, per_page: 20 };
    if (searchForm.search) newFilters.search = searchForm.search;
    if (searchForm.game_id) newFilters.game_id = searchForm.game_id;
    if (searchForm.rarity) newFilters.rarity = searchForm.rarity;
    if (searchForm.value_op && searchForm.value_amount !== '') {
      newFilters.value_op = searchForm.value_op as 'gt' | 'lt' | 'eq';
      newFilters.value_amount = parseFloat(searchForm.value_amount);
    }
    setFilters(newFilters);
    setShowSearch(false);
  };

  const handleSearchCancel = () => {
    setShowSearch(false);
  };

  const handleSearchClear = () => {
    setSearchForm({ search: '', game_id: 0, rarity: '', value_op: '', value_amount: '' });
    setFilters({ page: 1, per_page: 20 });
    setShowSearch(false);
  };

  const hasActiveFilters = !!(filters.search || filters.game_id || filters.rarity || filters.value_op);

  const showSaleAlert = (card: Card) => {
    presentAlert({
      header: 'Mark as Sold',
      subHeader: card.name,
      message: `Record a sale for "${card.name}"?`,
      inputs: [
        { name: 'salePrice', type: 'number', placeholder: 'Sale price', min: 0 },
        { name: 'notes', type: 'text', placeholder: 'Notes (optional)' },
      ],
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm Sale',
          handler: (data) => {
            const price = parseFloat(data.salePrice);
            createEvent.mutate({
              event_type: 'sale',
              card_id: card.id,
              card_name: card.name,
              game_name: card.game?.name || 'Unknown',
              quantity: 1,
              value: card.estimated_value,
              sale_price: isNaN(price) ? undefined : price,
              notes: data.notes || undefined,
            });
          },
        },
      ],
    });
  };

  const showLostAlert = (card: Card) => {
    presentAlert({
      header: 'Mark as Lost',
      subHeader: card.name,
      message: `Are you sure you want to mark "${card.name}" as lost?`,
      inputs: [
        { name: 'notes', type: 'text', placeholder: 'Notes (optional)' },
      ],
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm Lost',
          cssClass: 'alert-button-danger',
          handler: (data) => {
            createEvent.mutate({
              event_type: 'loss',
              card_id: card.id,
              card_name: card.name,
              game_name: card.game?.name || 'Unknown',
              quantity: 1,
              value: card.estimated_value,
              notes: data.notes || undefined,
            });
          },
        },
      ],
    });
  };

  const handleAddQuantity = (card: Card) => {
    presentAlert({
      header: 'Add Quantity',
      subHeader: card.name,
      message: `Current quantity: ${card.quantity}`,
      inputs: [
        { name: 'amount', type: 'number', placeholder: 'Amount to add', min: 1, value: 1 },
      ],
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Add',
          handler: (data) => {
            const amount = parseInt(data.amount, 10);
            if (!amount || amount < 1) return;
            updateCard.mutate({ id: card.id, data: { quantity: card.quantity + amount } });
          },
        },
      ],
    });
  };

  const handleTradeOut = (card: Card) => {
    history.push(`/trades/new?cardOut=${card.id}`);
  };



  const loadMore = async (e: CustomEvent) => {
    if (data && filters.page! < data.pages) {
      setFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }));
    }
    (e.target as HTMLIonInfiniteScrollElement).complete();
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color="primary" style={{ position: 'relative' }}>
          <IonTitle>My Cards</IonTitle>
          <IonIcon
            icon={searchOutline}
            onClick={() => {
              setSearchForm({
                search: filters.search || '',
                game_id: filters.game_id || 0,
                rarity: filters.rarity || '',
                value_op: filters.value_op || '',
                value_amount: filters.value_amount !== undefined ? String(filters.value_amount) : '',
              });
              setShowSearch(true);
            }}
            style={{ position: 'absolute', right: 52, top: '50%', transform: 'translateY(-50%)', fontSize: 24, cursor: 'pointer', color: hasActiveFilters ? '#ffc409' : '#fff', zIndex: 10 }}
          />
          <IonIcon
            icon={settingsOutline}
            onClick={() => history.push('/settings')}
            style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', fontSize: 24, cursor: 'pointer', color: '#fff', zIndex: 10 }}
          />
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {/* Full-screen search panel */}
        {showSearch && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
            background: 'var(--ion-background-color, #fff)',
            display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ background: 'var(--ion-color-primary)', color: '#fff', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Search & Filter</h2>
              <IonIcon icon={closeOutline} onClick={handleSearchCancel} style={{ fontSize: 28, cursor: 'pointer' }} />
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Card Name</label>
                <input
                  style={inputStyle}
                  placeholder="Search by name..."
                  value={searchForm.search}
                  onChange={(e) => setSearchForm((f) => ({ ...f, search: e.target.value }))}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Game</label>
                <select
                  style={selectStyle}
                  value={searchForm.game_id || ''}
                  onChange={(e) => setSearchForm((f) => ({ ...f, game_id: Number(e.target.value) || 0, rarity: '' }))}
                >
                  <option value="">All Games</option>
                  {games.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Rarity</label>
                <select
                  style={selectStyle}
                  value={searchForm.rarity}
                  onChange={(e) => setSearchForm((f) => ({ ...f, rarity: e.target.value }))}
                >
                  <option value="">All Rarities</option>
                  {selectedGame?.rarity_options.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Value ($)</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    style={{ ...selectStyle, flex: '0 0 auto', width: 80 }}
                    value={searchForm.value_op}
                    onChange={(e) => setSearchForm((f) => ({ ...f, value_op: e.target.value }))}
                  >
                    <option value="">--</option>
                    <option value="gt">&gt;</option>
                    <option value="lt">&lt;</option>
                    <option value="eq">=</option>
                  </select>
                  <input
                    style={{ ...inputStyle, flex: 1 }}
                    type="number"
                    step="0.01"
                    placeholder="Amount"
                    value={searchForm.value_amount}
                    onChange={(e) => setSearchForm((f) => ({ ...f, value_amount: e.target.value }))}
                    disabled={!searchForm.value_op}
                  />
                </div>
              </div>
            </div>
            <div style={{ padding: 16, display: 'flex', gap: 8 }}>
              <IonButton expand="block" fill="outline" color="medium" onClick={handleSearchClear} style={{ flex: 1 }}>
                Clear
              </IonButton>
              <IonButton expand="block" onClick={handleSearchApply} style={{ flex: 1 }}>
                Search
              </IonButton>
            </div>
          </div>
        )}

        <IonSegment
          value={viewMode}
          onIonChange={(e) => setViewMode(e.detail.value as 'list' | 'gallery')}
          style={{ margin: '0 16px 8px' }}
        >
          <IonSegmentButton value="list">
            <IonIcon icon={list} />
            <IonLabel>List</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="gallery">
            <IonIcon icon={grid} />
            <IonLabel>Gallery</IonLabel>
          </IonSegmentButton>
        </IonSegment>

        {isLoading ? (
          <div className="ion-text-center ion-padding"><IonSpinner /></div>
        ) : (
          <>
            {viewMode === 'list' ? (
              <IonList>
                {data?.items.map((card) => (
                  <CardItem
                    key={card.id}
                    card={card}
                    onClick={() => history.push(`/cards/${card.id}/edit`)}
                    onAddQuantity={handleAddQuantity}
                    onTradeOut={handleTradeOut}
                    onSale={showSaleAlert}
                    onLost={showLostAlert}
                  />
                ))}
                {data?.items.length === 0 && (
                  <div className="ion-text-center ion-padding">No cards found. Tap + to add your first card!</div>
                )}
              </IonList>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 8,
                padding: '8px 12px',
              }}>
                {data?.items.map((card) => {
                  const imgSrc = resolveImageUrl(card.image_front_url);
                  return (
                    <div
                      key={card.id}
                      onClick={() => history.push(`/cards/${card.id}/edit`)}
                      style={{
                        cursor: 'pointer',
                        borderRadius: 8,
                        overflow: 'hidden',
                        background: 'var(--ion-card-background, var(--ion-item-background, #fff))',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                      }}
                    >
                      <div style={{
                        width: '100%',
                        aspectRatio: '2.5 / 3.5',
                        background: 'var(--ion-color-light)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}>
                        {imgSrc ? (
                          <img
                            src={imgSrc}
                            alt={card.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <IonIcon icon={imageOutline} style={{ fontSize: 48, color: 'var(--ion-color-medium)' }} />
                        )}
                      </div>
                      <div style={{ padding: '6px 8px' }}>
                        <div style={{
                          fontWeight: 600, fontSize: 13,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {card.name}
                        </div>
                        <div style={{
                          fontSize: 12, color: 'var(--ion-color-medium)',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {card.set_name || card.game?.name || ''}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--ion-color-medium)', marginTop: 2 }}>
                          Qty: {card.quantity}
                          {(card.estimated_value != null || card.purchase_price != null) && ' ('}
                          {card.estimated_value != null && `$${card.estimated_value.toFixed(2)}`}
                          {card.estimated_value != null && card.purchase_price != null && ' / '}
                          {card.purchase_price != null && `$${card.purchase_price.toFixed(2)}`}
                          {(card.estimated_value != null || card.purchase_price != null) && ')'}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {data?.items.length === 0 && (
                  <div className="ion-text-center ion-padding" style={{ gridColumn: '1 / -1' }}>
                    No cards found. Tap + to add your first card!
                  </div>
                )}
              </div>
            )}
            <IonInfiniteScroll onIonInfinite={loadMore} disabled={!data || filters.page! >= data.pages}>
              <IonInfiniteScrollContent />
            </IonInfiniteScroll>
          </>
        )}

        <IonFab vertical="bottom" horizontal="end" slot="fixed">
          <IonFabButton onClick={() => history.push('/cards/new/edit')}>
            <IonIcon icon={add} />
          </IonFabButton>
        </IonFab>

      </IonContent>
    </IonPage>
  );
};

export default CardList;
